import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Upload, X } from "lucide-react";
import { EntryMethodIcon, ICON_LIBRARY } from "@/components/contest/EntryMethodIcon";

export interface EntryMethodInput {
  id?: string;
  key: string;
  label: string;
  description: string;
  entriesAwarded: number;
  actionUrl: string;
  iconType: string | null;
  iconKey: string | null;
  iconUrl: string | null;
  iconFile?: File | null;
  iconPath?: string | null;
  isEnabled: boolean;
}

interface EntryMethodEditorProps {
  method: EntryMethodInput;
  index: number;
  canDelete: boolean;
  onChange: (index: number, field: keyof EntryMethodInput, value: string | number | boolean | null) => void;
  onFileSelect?: (index: number, file: File) => void;
  onDelete: (index: number) => void;
}

export function EntryMethodEditor({
  method,
  index,
  canDelete,
  onChange,
  onFileSelect,
  onDelete
}: EntryMethodEditorProps) {
  const handleSwapSource = (source: "library" | "upload") => {
    if (source === "library") {
      onChange(index, "iconType", "library");
      // Keep URL if switching back? No, cleaner to clear if saving. 
      // But if user accidentally switched, we lose it?
      // Let's just switch type.
    } else {
      onChange(index, "iconType", "upload");
    }
  };

  return (
    <div className="p-4 bg-muted rounded-lg space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <EntryMethodIcon
              iconType={method.iconType}
              iconKey={method.iconKey}
              iconUrl={method.iconUrl}
              className="text-primary"
            />
          </div>
          <span className="text-sm font-medium text-muted-foreground">
            Method {index + 1}
          </span>
        </div>
        {canDelete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onDelete(index)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Label *</Label>
          <Input
            value={method.label}
            onChange={(e) => onChange(index, "label", e.target.value)}
            placeholder="Follow on Instagram"
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Entries Awarded</Label>
          <Input
            type="number"
            min={1}
            max={100}
            value={method.entriesAwarded}
            onChange={(e) => onChange(index, "entriesAwarded", parseInt(e.target.value) || 1)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Input
          value={method.description}
          onChange={(e) => onChange(index, "description", e.target.value)}
          placeholder="Follow us for bonus entries"
        />
      </div>

      <div className="space-y-2">
        <Label>Action URL (opens when clicked)</Label>
        <Input
          type="url"
          value={method.actionUrl}
          onChange={(e) => onChange(index, "actionUrl", e.target.value)}
          placeholder="https://instagram.com/yourpage"
        />
      </div>

      {/* Icon selection */}
      <div className="space-y-4 pt-2 border-t border-border/50">
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Icon</label>

        <div className="flex bg-muted rounded-md p-1 w-fit">
          <Button
            type="button"
            variant={method.iconType !== "upload" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => handleSwapSource("library")}
            className="h-8"
          >
            Library
          </Button>
          <Button
            type="button"
            variant={method.iconType === "upload" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => handleSwapSource("upload")}
            className="h-8"
          >
            Upload Custom
          </Button>
        </div>

        {method.iconType !== "upload" ? (
          <Select
            value={method.iconKey || "default"}
            onValueChange={(value) => onChange(index, "iconKey", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ICON_LIBRARY).map(([key, { icon: Icon, label }]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              {/* Preview */}
              <div className="w-12 h-12 rounded-lg border bg-background flex items-center justify-center overflow-hidden shrink-0">
                {method.iconUrl ? (
                  <img src={method.iconUrl} alt="Icon" className="w-full h-full object-cover" />
                ) : (
                  <Upload className="w-5 h-5 text-muted-foreground/50" />
                )}
              </div>

              {/* Upload Button */}
              <div className="flex-1">
                <Label
                  htmlFor={`icon-upload-${index}`}
                  className="flex items-center justify-center w-full px-4 py-2 h-10 border border-input rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">Choose Image</span>
                  <input
                    id={`icon-upload-${index}`}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.[0] && onFileSelect) {
                        onFileSelect(index, e.target.files[0]);
                      }
                    }}
                  />
                </Label>
                <p className="text-[10px] text-muted-foreground mt-1.5 ml-1">
                  Max 2MB. Recommendation: 64x64 PNG.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
